import { useState, useCallback, useRef, useEffect } from 'react'
import { Box, Input, List, ListItem, Text, Spinner } from '@chakra-ui/react'
import { useLoadScript } from '@react-google-maps/api'
import { useTranslation } from 'react-i18next'

const libraries: 'places'[] = ['places']

interface Location {
  lat: string
  lon: string
}

interface AddressInputProps {
  onLocationSelect?: (location: Location, address: string) => void
  onAddressSelect?: (address: string) => void
  label?: React.ReactNode
  inputData?: string
  isInvalid?: boolean
  errorMessage?: string
  /**
   * If true, only full address will be returned, no cities or streets without number
   */
  fullAddressOnly?: boolean
}

const AddressInput: React.FC<AddressInputProps> = ({
  onLocationSelect,
  onAddressSelect,
  label,
  inputData,
  isInvalid,
  errorMessage,
  fullAddressOnly = true,
}) => {
  const { t } = useTranslation()
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries,
  })

  const [inputValue, setInputValue] = useState(inputData ?? '')
  const [options, setOptions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([])
  const [showDropdown, setShowDropdown] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout>()
  const autocompleteService =
    useRef<google.maps.places.AutocompleteService | null>(null)
  const placesService = useRef<google.maps.places.PlacesService | null>(null)

  useEffect(() => {
    if (isLoaded) {
      autocompleteService.current = new google.maps.places.AutocompleteService()
      // We need a HTML element to create PlacesService
      placesService.current = new google.maps.places.PlacesService(
        document.createElement('div')
      )
    }
  }, [isLoaded])

  useEffect(() => {
    if (inputData) {
      setInputValue(inputData)
    }
  }, [inputData])

  const debouncedFetchAddresses = useCallback(
    (query: string) => {
      // Clear previous values
      onAddressSelect && onAddressSelect('')
      onLocationSelect && onLocationSelect({ lat: '', lon: '' }, '')

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      timeoutRef.current = setTimeout(() => {
        if (query.length >= 3 && autocompleteService.current) {
          autocompleteService.current.getPlacePredictions(
            {
              input: query,
              types: fullAddressOnly ? ['street_address'] : undefined,
            },
            (predictions, status) => {
              if (
                status === google.maps.places.PlacesServiceStatus.OK &&
                predictions
              ) {
                setOptions(predictions)
                setShowDropdown(true)
              }
            }
          )
        } else {
          setOptions([])
          setShowDropdown(false)
        }
      }, 1000)
    },
    [fullAddressOnly, onAddressSelect, onLocationSelect]
  )

  const handleSelect = (placeId: string, description: string) => {
    if (placesService.current) {
      if (!onLocationSelect) {
        onAddressSelect && onAddressSelect(description)
        setInputValue(description)
        setShowDropdown(false)
        return
      }
      placesService.current.getDetails(
        {
          placeId: placeId,
          fields: ['geometry', 'formatted_address'],
        },
        (place, status) => {
          if (
            status === google.maps.places.PlacesServiceStatus.OK &&
            place?.geometry?.location
          ) {
            const location = {
              lat: place.geometry.location.lat().toString(),
              lon: place.geometry.location.lng().toString(),
            }
            setInputValue(description)
            setShowDropdown(false)
            onLocationSelect(location, description)
          }
        }
      )
    }
  }

  if (loadError) {
    return (
      <Box position="relative" mt={4}>
        {label && <Text color={'brand.lightGray'}>{label}</Text>}
        <Input
          isDisabled
          placeholder="Google Maps API unavailable"
          _placeholder={{ color: 'red.500' }}
          isInvalid={true}
          mt={1}
          background={'gray.100'}
        />
        <Box color="red.500" fontSize="sm" mt={1}>
          {t('shared.errors.addressSearchNotAvailable')}
        </Box>
      </Box>
    )
  }

  return (
    <Box position="relative" w="100%">
      {label && (
        <Text fontSize="sm" fontWeight="medium" color="gray.700" mb={2}>
          {label}
        </Text>
      )}
      <Box position="relative">
        <Input
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value)
            debouncedFetchAddresses(e.target.value)
          }}
          placeholder={
            isLoaded
              ? t('shared.common.searchAddress')
              : t('shared.common.loading')
          }
          _placeholder={{ color: 'gray.400' }}
          isInvalid={isInvalid}
          isDisabled={!isLoaded}
          color={'gray.700'}
          background={'white'}
          size="lg"
          py={2}
          px={4}
          borderRadius="lg"
          borderColor="gray.300"
          _hover={{ borderColor: 'purple.300' }}
          _focus={{
            borderColor: 'purple.500',
            boxShadow: '0 0 0 1px var(--chakra-colors-purple-500)',
          }}
          aria-label={typeof label === 'string' ? label : 'Address input'}
        />
        {!isLoaded && (
          <Box
            position="absolute"
            right="10px"
            top="50%"
            transform="translateY(-50%)"
          >
            <Spinner size="sm" color="purple.500" />
          </Box>
        )}
      </Box>
      {isInvalid && errorMessage && (
        <Box color="red.500" fontSize="sm" mt={2}>
          {errorMessage}
        </Box>
      )}
      {showDropdown && options.length > 0 && (
        <List
          position="absolute"
          w="100%"
          bg="white"
          boxShadow="xl"
          borderRadius="lg"
          border="1px solid"
          borderColor="gray.200"
          mt={2}
          maxH="250px"
          overflowY="auto"
          zIndex={1000}
          css={{
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '10px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#888',
              borderRadius: '10px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#555',
            },
          }}
        >
          {options.map((place) => (
            <ListItem
              key={place.place_id}
              p={3}
              color="gray.700"
              fontSize="sm"
              borderBottom="1px solid"
              borderColor="gray.100"
              transition="all 0.2s"
              _hover={{
                bg: 'purple.50',
                color: 'purple.700',
                cursor: 'pointer',
                fontWeight: 'medium',
              }}
              _last={{ borderBottom: 'none' }}
              onClick={() => handleSelect(place.place_id, place.description)}
            >
              {place.description}
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  )
}

export default AddressInput
